/**
 * Reprise — spiller av lagrede spor for en regatta. Tidslinje med play/pause og
 * hastighet; viser hver båts posisjon ved valgt tidspunkt med en hale.
 *
 * Regattaklokke: tidslinja går fra varselsignalet (5 min før start) til siste båt
 * er i mål. Klokka teller ned med flaggene som er oppe og viser så tid etter start;
 * båtlista viser plass og tid etter hvert som båtene går i mål.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { api } from '../lib/api.ts';
import { colorForSail } from '../lib/trackColors.ts';
import { boatIcon } from '../lib/boatMarker.ts';
import { createCourseLayer, lopRader, type CourseLayer } from '../lib/courseLayer.ts';
import { enableWakeLock, disableWakeLock } from '../lib/wakelock.ts';
import { maalgangerPerSeil, sailKey, sorterMedMaal } from '../lib/maalganger.ts';
import { formatElapsed } from '../components/FinishTiming.tsx';
import { LopListe, RegattaKlokke } from '../components/KartPaneler.tsx';

const DEFAULT_CENTER: [number, number] = [58.34, 8.59];
const KV_ATTR = '© Kartverket';
const TRAIL_MS = 5 * 60_000;
const SPEEDS = [1, 4, 16, 60];
/** Reprisen begynner ved varselsignalet. */
const PRESTART_MS = 5 * 60_000;

interface P {
  t: number;
  lat: number;
  lon: number;
  sogKn: number | null;
}
interface Track {
  sailNumber: string;
  color: string;
  pts: P[];
}
interface Layer {
  marker: L.Marker;
  line: L.Polyline;
}

function fmtClock(ms: number): string {
  return new Date(ms).toLocaleTimeString('no-NO', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: 'Europe/Oslo',
  });
}

export function ReplayPage() {
  const { slug } = useParams<{ slug: string }>();
  const mapEl = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layers = useRef<Map<string, Layer>>(new Map());
  const courseLayerRef = useRef<CourseLayer | null>(null);
  const boundsRef = useRef<L.LatLngBounds | null>(null);
  const didFitRef = useRef(false);

  function fitOnce() {
    const map = mapRef.current;
    const el = mapEl.current;
    if (!map || !el || !boundsRef.current || didFitRef.current) return;
    if (el.clientWidth < 2 || el.clientHeight < 2) return; // vent til fanen har størrelse
    map.invalidateSize();
    map.fitBounds(boundsRef.current.pad(0.2), { maxZoom: 16 });
    didFitRef.current = true;
  }

  const full = useQuery({
    queryKey: ['track-full', slug],
    queryFn: () => api.trackFull(slug!),
    enabled: !!slug,
  });
  const course = useQuery({
    queryKey: ['course', slug],
    queryFn: () => api.raceCourse(slug!),
    enabled: !!slug,
  });
  // Klasseflaggene (til klokka) og målgangene (siste båt i mål = slutten på reprisen)
  const raceInfo = useQuery({
    queryKey: ['race', slug],
    queryFn: () => api.race(slug!),
    enabled: !!slug,
  });
  const finish = useQuery({
    queryKey: ['race-finish', slug],
    queryFn: () => api.finishList(slug!),
    enabled: !!slug,
  });

  const tracks = useMemo<Track[]>(() => {
    if (!full.data) return [];
    return full.data.boats
      .map((b) => ({
        sailNumber: b.sailNumber,
        color: colorForSail(b.sailNumber),
        pts: b.trail
          .map((p) => ({ t: new Date(p.recordedAt).getTime(), lat: p.lat, lon: p.lon, sogKn: p.sogKn }))
          .sort((a, z) => a.t - z.t),
      }))
      .filter((t) => t.pts.length > 0);
  }, [full.data]);

  const startMs = full.data ? new Date(full.data.race.startTime).getTime() : NaN;

  // Målganger med tid, per seilnummer. GPS-forslaget brukes når komiteen ikke har
  // ført tid (men ikke når raden har en merknad, f.eks. DNF).
  const finishes = useMemo(() => maalgangerPerSeil(finish.data?.records), [finish.data]);

  const klasseflagg = useMemo(() => {
    const flagg = [...new Set((raceInfo.data?.classes ?? []).map((c) => c.flag))];
    return flagg.length ? flagg : ['G'];
  }, [raceInfo.data]);

  const lop = useMemo(() => lopRader(course.data), [course.data]);

  const range = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;
    for (const t of tracks)
      for (const p of t.pts) {
        if (p.t < min) min = p.t;
        if (p.t > max) max = p.t;
      }
    if (!tracks.length) return null;
    // Regattaklokke når starttiden passer med sporene (serveren henter spor fra
    // 3 t før start; ligger de helt andre steder, er starttiden endret etterpå):
    // fra varselsignalet til siste båt er i mål — uten målganger til sporene slutter.
    if (Number.isFinite(startMs) && startMs >= min - 3 * 3_600_000 && startMs - PRESTART_MS < max) {
      let last = -Infinity;
      for (const f of finishes.values()) last = Math.max(last, startMs + f.elapsedMs);
      return {
        min: startMs - PRESTART_MS,
        max: last > startMs - PRESTART_MS ? last : max,
        clock: true,
        lastFinishAt: Number.isFinite(last) ? last : null,
      };
    }
    return { min, max, clock: false, lastFinishAt: null };
  }, [tracks, startMs, finishes]);

  const [cursor, setCursor] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(16);
  const [awake, setAwake] = useState(false);

  // Bare når starten på tidslinja flytter seg (ikke når målgangene kommer inn).
  const rangeMin = range?.min;
  useEffect(() => {
    if (rangeMin != null) setCursor(rangeMin);
  }, [rangeMin]);

  // Init kart
  useEffect(() => {
    if (!mapEl.current || mapRef.current) return;
    const el = mapEl.current;
    const map = L.map(el).setView(DEFAULT_CENTER, 12);
    const graatone = L.tileLayer(
      'https://cache.kartverket.no/v1/wmts/1.0.0/topograatone/default/webmercator/{z}/{y}/{x}.png',
      { maxZoom: 18, attribution: KV_ATTR },
    );
    const sjokart = L.tileLayer(
      'https://cache.kartverket.no/v1/wmts/1.0.0/sjokartraster/default/webmercator/{z}/{y}/{x}.png',
      { maxZoom: 18, attribution: KV_ATTR },
    );
    graatone.addTo(map);
    L.control.layers({ Gråtone: graatone, Sjøkart: sjokart }, {}, { collapsed: true }).addTo(map);
    mapRef.current = map;
    courseLayerRef.current = createCourseLayer(map);
    // Når fanen får størrelse (typisk ny bakgrunns-fane på mobil), oppdater
    // kartet og sentrer på sporene FØRST da — ellers zoomer fitBounds helt inn
    // på et 0-px kart og du får en blank/hvit flis.
    const ro = new ResizeObserver(() => {
      map.invalidateSize();
      fitOnce();
    });
    ro.observe(el);
    const t = setTimeout(() => map.invalidateSize(), 100);
    return () => {
      clearTimeout(t);
      ro.disconnect();
      map.remove();
      mapRef.current = null;
      courseLayerRef.current = null;
      layers.current.clear();
      didFitRef.current = false;
    };
  }, []);

  // Lagre omfanget og sentrer på sporene (først når fanen har størrelse)
  useEffect(() => {
    if (tracks.length === 0) return;
    const pts: [number, number][] = [];
    for (const t of tracks) for (const p of t.pts) pts.push([p.lat, p.lon]);
    if (!pts.length) return;
    boundsRef.current = L.latLngBounds(pts);
    fitOnce();
  }, [tracks]);

  // Tegn bane (merker + start-/mållinje) — nåværende posisjoner
  useEffect(() => {
    courseLayerRef.current?.render(course.data);
  }, [course.data]);

  // Ramme inn banen når det ikke finnes spor (så løypa vises likevel)
  useEffect(() => {
    const map = mapRef.current;
    const el = mapEl.current;
    if (!map || !el || el.clientWidth < 2 || el.clientHeight < 2) return;
    if (didFitRef.current || tracks.length > 0) return;
    const pts: [number, number][] = [];
    for (const m of course.data?.marks ?? []) pts.push([m.lat, m.lon]);
    for (const p of course.data?.startLine ?? []) pts.push([p.lat, p.lon]);
    for (const p of course.data?.finishLine ?? []) pts.push([p.lat, p.lon]);
    if (pts.length === 0) return;
    map.invalidateSize();
    map.fitBounds(L.latLngBounds(pts).pad(0.3), { maxZoom: 16 });
    didFitRef.current = true;
  }, [course.data, tracks]);

  // Avspilling
  useEffect(() => {
    if (!playing || !range) return;
    const step = 200;
    const id = setInterval(() => {
      setCursor((c) => {
        const next = c + step * speed;
        if (next >= range.max) {
          setPlaying(false);
          return range.max;
        }
        return next;
      });
    }, step);
    return () => clearInterval(id);
  }, [playing, speed, range]);

  // Hold skjermen våken så lenge reprisen er åpen og har spor å vise. Slippes
  // automatisk når man forlater siden. Re-akkvireres når fanen blir synlig igjen
  // (håndteres i wakelock-hjelperen).
  const harSpor = !!range;
  useEffect(() => {
    if (!harSpor) return;
    let cancelled = false;
    void (async () => {
      const ok = await enableWakeLock();
      if (!cancelled) setAwake(ok);
    })();
    return () => {
      cancelled = true;
      setAwake(false);
      void disableWakeLock();
    };
  }, [harSpor]);

  // Tegn posisjoner ved cursor
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !range) return;

    for (const tr of tracks) {
      const pts = tr.pts;
      let curIdx = -1;
      for (let i = 0; i < pts.length; i++) {
        if (pts[i]!.t <= cursor) curIdx = i;
        else break;
      }
      let layer = layers.current.get(tr.sailNumber);

      if (curIdx < 0) {
        // Båten har ikke startet ennå ved dette tidspunktet
        if (layer) {
          map.removeLayer(layer.marker);
          map.removeLayer(layer.line);
          layers.current.delete(tr.sailNumber);
        }
        continue;
      }

      const cur = pts[curIdx]!;
      const trail: [number, number][] = [];
      for (let i = curIdx; i >= 0; i--) {
        if (pts[i]!.t >= cursor - TRAIL_MS) trail.unshift([pts[i]!.lat, pts[i]!.lon]);
        else break;
      }
      const pos: [number, number] = [cur.lat, cur.lon];

      if (!layer) {
        const marker = L.marker(pos, { icon: boatIcon(tr.sailNumber, tr.color) }).addTo(map);
        const line = L.polyline(trail, { color: tr.color, weight: 3, opacity: 0.75 }).addTo(map);
        layers.current.set(tr.sailNumber, { marker, line });
      } else {
        layer.marker.setLatLng(pos);
        layer.line.setLatLngs(trail);
      }
    }
  }, [cursor, tracks, range]);

  function focusBoat(tr: Track) {
    const map = mapRef.current;
    if (!map) return;
    let idx = -1;
    for (let i = 0; i < tr.pts.length; i++) {
      if (tr.pts[i]!.t <= cursor) idx = i;
      else break;
    }
    const p = idx >= 0 ? tr.pts[idx]! : tr.pts[tr.pts.length - 1];
    if (p) map.setView([p.lat, p.lon], 16);
  }

  const raceName = full.data?.race.name ?? slug;
  const hasData = !!range;

  // Båtlista: båtene som er i mål ved tidspunktet først (i rekkefølge), så resten.
  const boatList = sorterMedMaal(
    tracks.map((t) => {
      const f = range?.clock ? finishes.get(sailKey(t.sailNumber)) : undefined;
      return { t, sailNumber: t.sailNumber, maal: f && cursor >= startMs + f.elapsedMs ? f : null };
    }),
  );

  return (
    <div
      className="fixed inset-x-0 top-0 z-20 h-screen flex flex-col bg-slate-950"
      style={{ height: '100dvh' }}
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-800">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/" className="text-slate-400 text-sm shrink-0 whitespace-nowrap">&larr; Regattaer</Link>
          <h1 className="font-semibold truncate">⏮ Reprise — {raceName}</h1>
        </div>
        <Link to={`/r/${slug}`} className="text-xs text-sky-400 shrink-0">Regatta</Link>
      </div>

      <div className="flex-1 relative">
        <div ref={mapEl} className="absolute inset-0" />

        {tracks.length > 0 && (
          <div className="absolute top-2 right-2 z-[1000] bg-slate-900/85 border border-slate-700 rounded-lg p-2 max-h-[55%] overflow-auto text-xs">
            <div className="text-slate-400 mb-1">{tracks.length} båter · trykk for å zoome</div>
            {boatList.map(({ t, maal }) => (
              <button
                key={t.sailNumber}
                onClick={() => focusBoat(t)}
                className="flex items-center gap-2 py-0.5 w-full hover:text-sky-300"
                title="Zoom til båten"
              >
                <span
                  className="w-3 h-3 rounded-full border border-white/70 shrink-0"
                  style={{ background: t.color }}
                />
                <span className="font-mono">{t.sailNumber}</span>
                {maal && (
                  <span className="ml-auto pl-3 font-mono tabular-nums text-emerald-300">
                    {maal.position != null && `${maal.position}. `}
                    {formatElapsed(maal.elapsedMs)}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
        {(lop.length > 0 || range?.clock) && (
          <div className="absolute left-2 bottom-2 z-[1000] pointer-events-none flex flex-col items-start gap-2">
            {lop.length > 0 && <LopListe rader={lop} />}
            {range?.clock && (
              <RegattaKlokke
                msToStart={startMs - cursor}
                iMaal={range.lastFinishAt != null && cursor >= range.lastFinishAt}
                klasseflagg={klasseflagg}
              />
            )}
          </div>
        )}
        {full.isLoading && (
          <div className="absolute inset-0 flex items-center justify-center text-slate-400">
            Laster spor…
          </div>
        )}
        {!full.isLoading && !hasData && (
          <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-sm">
            Ingen lagrede spor for denne regattaen.
          </div>
        )}
      </div>

      {hasData && range && (
        <div className="border-t border-slate-800 px-3 py-3 space-y-2">
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                if (cursor >= range.max) setCursor(range.min);
                setPlaying((p) => !p);
                void enableWakeLock().then(setAwake);
              }}
              className="px-4 py-2 bg-sky-600 hover:bg-sky-500 rounded-lg font-semibold text-sm shrink-0"
            >
              {playing ? '⏸ Pause' : '▶ Spill'}
            </button>
            <input
              type="range"
              min={range.min}
              max={range.max}
              value={cursor}
              onChange={(e) => {
                setPlaying(false);
                setCursor(Number(e.target.value));
              }}
              className="flex-1 accent-sky-500"
            />
            <span className="font-mono text-sm tabular-nums shrink-0">{fmtClock(cursor)}</span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-slate-500">Hastighet:</span>
            {SPEEDS.map((s) => (
              <button
                key={s}
                onClick={() => setSpeed(s)}
                className={`px-2 py-0.5 rounded ${
                  speed === s ? 'bg-sky-600 text-white' : 'bg-slate-800 text-slate-300'
                }`}
              >
                {s}×
              </button>
            ))}
            <span className="ml-auto text-slate-500">
              {awake && <span className="text-emerald-400 mr-2">📱 skjerm på</span>}
              {tracks.length} båter
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
